import React from 'react';
import { Checkbox, Col, Grid, NumberInput, Radio, Row, SelectPicker, Message } from 'rsuite';
import ZonedDatePicker from '@/components/ZonedDatePicker';
import { LatLng } from '@/models/core.model';
import { RunEventDistanceTypeEnum, RunEventDistanceTypeList } from '@/models/run-event-distance.model';
import { ApproveScrapedDistance, ScrapedEventDistance } from '@/models/scraped-event.model';
import { utc_iana_time_zone_id } from '@/constants/time';

// One editable row per scraped race.
export interface ScrapedDistanceRow {
  include: boolean;
  name?: string;
  distanceKm?: number;
  type: RunEventDistanceTypeEnum;
  startDate?: Date;
}

const pathLengthKm = (path?: LatLng[]) => {
  if (!path || path.length < 2) return 0;
  const rad = (d: number) => (d * Math.PI) / 180;
  let km = 0;
  for (let i = 1; i < path.length; i++) {
    const a = path[i - 1], b = path[i];
    const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 +
      Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
    km += 2 * 6371 * Math.asin(Math.sqrt(h));
  }
  return km;
};

const typeForKm = (km?: number): RunEventDistanceTypeEnum => {
  if (!km) return RunEventDistanceTypeEnum.Other;
  if (Math.abs(km - 42.195) < 0.5) return RunEventDistanceTypeEnum.Full;
  if (Math.abs(km - 21.0975) < 0.3) return RunEventDistanceTypeEnum.Half;
  if (Math.abs(km - 10) < 0.1) return RunEventDistanceTypeEnum.TenKm;
  return RunEventDistanceTypeEnum.Other;
};

export const rowsFromScraped = (distances: ScrapedEventDistance[] | undefined, withType: boolean): ScrapedDistanceRow[] =>
  (distances ?? []).map(d => {
    const km = d.distanceInMeter ? d.distanceInMeter / 1000 : undefined;
    const type = withType ? typeForKm(km) : RunEventDistanceTypeEnum.Other;
    return { include: true, name: d.name, distanceKm: km, type, startDate: d.startDate };
  });

// The race whose distance is closest to the route length gets the route by default.
export const defaultRouteIndex = (rows: ScrapedDistanceRow[], path?: LatLng[]): number | undefined => {
  const routeKm = pathLengthKm(path);
  if (!routeKm || rows.length === 0) return undefined;
  let best = 0;
  rows.forEach((r, i) => {
    if (Math.abs((r.distanceKm ?? 0) - routeKm) < Math.abs((rows[best].distanceKm ?? 0) - routeKm)) best = i;
  });
  return best;
};

// Returns an error message, or the request items.
export const rowsToRequest = (rows: ScrapedDistanceRow[], routeIndex: number | undefined, path?: LatLng[])
  : string | ApproveScrapedDistance[] => {
  const out: ApproveScrapedDistance[] = [];
  for (const [i, r] of rows.entries()) {
    if (!r.include) continue;
    if (!r.startDate) return `Start date is required for "${r.name ?? 'race ' + (i + 1)}".`;
    if (r.type === RunEventDistanceTypeEnum.Other && !(r.distanceKm && r.distanceKm > 0))
      return `Distance is required for "${r.name ?? 'race ' + (i + 1)}".`;
    out.push({
      startDate: r.startDate,
      type: r.type,
      distance: r.distanceKm ? Math.round(r.distanceKm * 1000) : undefined,
      path: i === routeIndex && path && path.length > 0 ? path : undefined,
    });
  }
  return out;
};

interface Props {
  rows: ScrapedDistanceRow[];
  onChange: (rows: ScrapedDistanceRow[]) => void;
  routeIndex?: number;
  onRouteIndexChange: (i: number | undefined) => void;
  hasRoute: boolean;
  // Marathons pick Full/Half/10K; run events only use the distance.
  showType: boolean;
  zoneIanaId?: string;
  disabled?: boolean;
}

const ScrapedDistancesEditor: React.FC<Props> = ({ rows, onChange, routeIndex, onRouteIndexChange, hasRoute, showType, zoneIanaId, disabled }) => {
  const update = (i: number, patch: Partial<ScrapedDistanceRow>) =>
    onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <>
      <Message showIcon type="info" style={{ marginBottom: 10 }}>
        One distance is created for each ticked race. Distances without a route start and end at the city location.
        {hasRoute && ' Pick which race the scraped route belongs to.'}
      </Message>
      <Grid fluid>
        <Row style={{ fontWeight: 600, marginBottom: 6 }}>
          <Col span={{ xs: 2 }}>Add</Col>
          <Col span={{ xs: showType ? 5 : 9 }}>Race</Col>
          {showType && <Col span={{ xs: 4 }}>Type</Col>}
          <Col span={{ xs: 4 }}>Distance (km)</Col>
          <Col span={{ xs: 7 }}>Start Date</Col>
          {hasRoute && <Col span={{ xs: 2 }}>Route</Col>}
        </Row>
        {rows.map((r, i) => (
          <Row key={i} style={{ marginBottom: 8, display: 'flex', alignItems: 'center' }}>
            <Col span={{ xs: 2 }}>
              <Checkbox checked={r.include} disabled={disabled}
                onChange={(_, chk) => {
                  update(i, { include: chk });
                  if (!chk && routeIndex === i) onRouteIndexChange(undefined);
                }} />
            </Col>
            <Col span={{ xs: showType ? 5 : 9 }}>{r.name ?? '-'}</Col>
            {showType && <Col span={{ xs: 4 }}>
              <SelectPicker block searchable={false} cleanable={false} disabled={disabled || !r.include}
                data={RunEventDistanceTypeList} value={r.type}
                onChange={v => update(i, { type: (v ?? RunEventDistanceTypeEnum.Other) as RunEventDistanceTypeEnum })} />
            </Col>}
            <Col span={{ xs: 4 }}>
              <NumberInput min={0} step={0.1} disabled={disabled || !r.include}
                value={r.distanceKm ?? ''}
                onChange={v => update(i, { distanceKm: v === '' || v == null ? undefined : Number(v) })} />
            </Col>
            <Col span={{ xs: 7 }}>
              <ZonedDatePicker block disabled={disabled || !r.include}
                value={r.startDate ?? null}
                onChange={dt => update(i, { startDate: dt ?? undefined })}
                zoneIanaId={zoneIanaId ?? utc_iana_time_zone_id} />
            </Col>
            {hasRoute && (
              <Col span={{ xs: 2 }}>
                <Radio checked={routeIndex === i} disabled={disabled || !r.include}
                  onChange={() => onRouteIndexChange(i)} />
              </Col>
            )}
          </Row>
        ))}
      </Grid>
    </>
  );
};

export default ScrapedDistancesEditor;
